<script setup lang="ts">
  import { type CalendarSize } from './Calendar.types';
  import { useCalendar } from './composables/useCalendar';
  import { Button } from '@/components';

  interface CalendarFooterProps {
    range?: boolean;
    mobile?: boolean;
    double?: boolean;
    size?: `${CalendarSize}`;
  }

  interface CalendarFooterEmits {
    (e: 'apply'): void;
    (e: 'reset'): void;
  }

  defineProps<CalendarFooterProps>();
  const emit = defineEmits<CalendarFooterEmits>();

  const { submitText, displayedDate } = useCalendar();

  const onReset = () => {
    emit('reset');
    displayedDate.value = Date.now();
  };

  const onApply = () => {
    emit('apply');
  };
</script>

<template>
  <div
    :class="[
      'tl-calendar-footer',
      `tl-calendar-footer_size-${size}`,
      { 'tl-calendar-footer_mobile': mobile, 'tl-calendar-footer_column': range && !double },
    ]"
  >
    <div v-if="range && $slots.toolbar" class="tl-calendar-footer__toolbar">
      <slot name="toolbar" />
    </div>

    <div class="tl-calendar-footer__buttons">
      <Button appearance="tertiary" type="icon" icon="refresh" :size="size" @click="onReset" />
      <Button :size="size" class="apply-button" @click="onApply">{{ submitText }}</Button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .tl-calendar-footer {
    $root: &;

    align-items: center;
    background: var(--color-bg-primary);
    border-top: 1px dashed var(--color-text-tertiary);
    display: flex;
    justify-content: space-between;
    padding-top: var(--spacing-s);

    &_mobile {
      bottom: 0;
      position: sticky;
      z-index: 5;
    }

    &_column,
    &_mobile {
      flex-direction: column;
      gap: var(--spacing-s);

      #{$root}__toolbar {
        border-bottom: 1px dashed var(--color-text-tertiary);
        justify-content: center;
        padding-bottom: var(--spacing-s);
        width: 100%;
      }

      #{$root}__buttons {
        margin-left: unset;
        max-width: none;
        width: 100%;

        .apply-button {
          width: 100%;
        }
      }
    }

    &_size-l {
      #{$root}__buttons {
        max-width: 432px;
      }
    }

    &__toolbar {
      align-items: center;
      display: flex;
      gap: var(--spacing-xs);
    }

    &__buttons {
      align-items: center;
      display: flex;
      gap: var(--spacing-xl);
      margin-left: auto;
      max-width: 320px;
      width: 100%;

      .apply-button {
        width: 100%;
      }
    }
  }
</style>
